import React, { useState } from 'react';
import NodeCredentialsPanel from './NodeCredentialsPanel';
import SectionedDialog from './SectionedDialog';

/** Add Network modal (multi-network slug Phase 3).
 *
 * POSTs to /api/networks/ — atomic network row + first node mint on
 * the hub. Body shape:
 *   {canonical_name, display_name, node: {name, internal_host}}
 *
 * v1 limit (intentional): first node is **in-stack only**. The hub's
 * create_network_with_root_node SP doesn't yet mint the app-identity
 * member a standalone substrate needs. After creating the network,
 * operators who want a standalone root use Add Node → Standalone with
 * the new network's root as parent — or extend the hub SP in a
 * follow-up. Documented inline so the in-stack-only choice is
 * surfaced to the operator, not silent.
 *
 * On success: shows the freshly-minted node's credentials in the same
 * panel AddNodeModal uses (the api_key + node.token reveal moment).
 */
export default function AddNetworkModal({ onClose, onSuccess }) {
  const [canonicalName, setCanonicalName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [nodeName, setNodeName] = useState('');
  const [internalHost, setInternalHost] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // admin-sectioned-dialogs: active category pane.
  const [activeCat, setActiveCat] = useState('network');

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!canonicalName.trim()) { setActiveCat('network'); setError('Canonical name is required.'); return; }
    if (!displayName.trim()) { setActiveCat('network'); setError('Display name is required.'); return; }
    if (!nodeName.trim()) { setActiveCat('root'); setError('Node name is required.'); return; }
    if (!internalHost.trim()) {
      setActiveCat('root');
      setError('Internal host (docker service name) is required for in-stack root nodes.');
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const body = {
        canonical_name: canonicalName.trim(),
        display_name: displayName.trim(),
        node: { name: nodeName.trim(), internal_host: internalHost.trim() },
      };
      const r = await fetch('/api/networks/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await r.text();
      let parsed = null;
      try { parsed = JSON.parse(text); } catch (_) {}
      if (!r.ok) {
        const hint = (
          r.status === 409 ? ' — canonical_name already in use; pick another' :
          r.status === 400 ? ' — slug is reserved or malformed' :
          ''
        );
        throw new Error((parsed && (parsed.message || parsed.error) || text || `HTTP ${r.status}`) + hint);
      }
      // BFF passes through hub's {code, type, entity} envelope where
      // entity = the full create_network_with_root_node JSON
      // (top-level node fields + a nested `network` block).
      const entity = (parsed && parsed.entity) || parsed;
      setResult(entity);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  const renderNetwork = () => (
    <>
            <label style={labelStyle}>
              <span>Canonical name <small style={{ color: '#888' }}>(URL slug)</small></span>
              <input value={canonicalName} onChange={(e) => setCanonicalName(e.target.value)}
                     autoFocus maxLength={63} required
                     placeholder="acme  (a-z, 0-9, dash)" style={inputStyle} />
              <small style={{ color: '#888', fontSize: '0.75rem' }}>
                Lowercase kebab-case. Used in friendly URLs:{' '}
                <code>aveq.io/&lt;name&gt;/&lt;station_id&gt;</code> →{' '}
                <code>/s/&lt;network_id&gt;/&lt;station_id&gt;</code>. Reserved:{' '}
                <code>api</code>, <code>chat</code>, <code>s</code>, <code>health</code>, <code>admin</code>, <code>hub</code>, <code>connect</code>.
              </small>
            </label>

            <label style={labelStyle}>
              <span>Display name</span>
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                     maxLength={120} required
                     placeholder="Acme Corp" style={inputStyle} />
            </label>
    </>
  );

  const renderRoot = () => (
            <div>
              <p style={{ color: '#888', fontSize: '0.75rem', margin: '0 0 12px 0' }}>
                The network's first node is <strong>in-stack only</strong> for now. Standalone-substrate root
                nodes (docker / bare-metal recipients) aren't yet supported through this flow — add the
                network first, then use <strong>Add Node</strong> to mint a standalone child under the new root.
              </p>

              <label style={labelStyle}>
                <span>Node name</span>
                <input value={nodeName} onChange={(e) => setNodeName(e.target.value)}
                       maxLength={64} required
                       placeholder="e.g. acme-root" style={inputStyle} />
              </label>

              <label style={labelStyle}>
                <span>Internal host <small style={{ color: '#888' }}>(docker service name)</small></span>
                <input value={internalHost} onChange={(e) => setInternalHost(e.target.value)}
                       required
                       placeholder="e.g. acme-qbric" style={inputStyle} />
                <small style={{ color: '#888', fontSize: '0.75rem' }}>
                  Parent dials the root via <code>ws://&lt;internal_host&gt;:4567/s/link</code> instead of routing
                  through the rendezvous.
                </small>
              </label>
            </div>
  );

  const categories = [
    { id: 'network', label: 'Network', description: 'Creates a new network. Child nodes added later inherit it automatically.', hasRequired: true, complete: canonicalName.trim() !== '' && displayName.trim() !== '', render: renderNetwork },
    { id: 'root', label: 'Root node', description: 'The network first (root) node — in-stack only for now.', hasRequired: true, complete: nodeName.trim() !== '' && internalHost.trim() !== '', render: renderRoot },
  ];

  const resultNode = result ? (
    <>
      <h2 style={{ marginTop: 0, color: '#1e8e3e' }}>Network created</h2>
      <div style={{ background: '#f6f8fa', padding: 12, borderRadius: 4, marginBottom: 12, fontSize: '0.85rem' }}>
        <div><strong>Network</strong> <code>{result.network && result.network.canonical_name}</code> (id {result.network && result.network.id})</div>
        <div style={{ color: '#888', marginTop: 4 }}>
          Root node <code>{result.name}</code> (id {result.node_id}). Future child nodes inherit this network automatically.
        </div>
      </div>
      <NodeCredentialsPanel
        credentials={result}
        warning="⚠ Save the api_key + token into the new node's api-config.yml. Credentials can also be re-fetched from the node detail panel later." />
    </>
  ) : null;

  const resultFooter = (
    <button type="button" onClick={() => { onSuccess && onSuccess(); }} style={buttonPrimaryStyle}>Done</button>
  );

  return (
    <SectionedDialog
      title="Add network"
      categories={categories}
      activeId={activeCat}
      onSelect={setActiveCat}
      primaryLabel="Create network"
      submittingLabel="Creating…"
      submitting={submitting}
      onPrimary={submit}
      onCancel={onClose}
      error={error}
      result={resultNode}
      resultFooter={resultFooter}
      width={640}
    />
  );
}


// --- styles (match AddNodeModal palette) ---

const labelStyle = {
  display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12,
  fontSize: '0.85rem',
};
const inputStyle = {
  padding: '6px 8px', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: 4,
};
const buttonPrimaryStyle = {
  padding: '8px 16px', background: '#5fa67a', color: '#fff', border: 'none',
  borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
};
